import { createBrowserClient } from '@supabase/ssr';
import { User, Session, AuthError, Provider, AuthChangeEvent } from '@supabase/supabase-js';

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;

if (!supabaseUrl) {
  throw new Error('Missing REACT_APP_SUPABASE_URL environment variable');
}

if (!supabaseAnonKey) {
  throw new Error('Missing REACT_APP_SUPABASE_ANON_KEY environment variable');
}

// Determine if running on a .wendai.ai domain for cross-subdomain cookie sharing
const isWendaiDomain = typeof window !== 'undefined' && window.location.hostname.endsWith('.wendai.ai');

// Create Supabase client using @supabase/ssr for cookie-based session storage.
// When running on *.wendai.ai, cookies are scoped to .wendai.ai so all subdomains
// (e.g. accounts.wendai.ai, app.wendai.ai) share the same auth session.
export const supabase = createBrowserClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true,
    flowType: 'pkce',
  },
  cookieOptions: {
    domain: isWendaiDomain ? '.wendai.ai' : undefined,
    path: '/',
    sameSite: 'lax' as const,
    secure: isWendaiDomain,
  },
});

// Types for user metadata
interface UserMetadata {
  name?: string;
  email?: string;
  phoneNumber?: string;
  username?: string;
  jobRole?: string;
  clients?: string[];
  full_name?: string;
  phone?: string;
  mobile?: string;
  user_name?: string;
  userName?: string;
  role?: string;
  status?: string;
  avatar_url?: string;
  profile_pic?: string;
}

// Response type for all auth operations
export interface AuthResponse {
  status: 'success' | 'error';
  data?: {
    user?: User | null;
    session?: Session | null;
    url?: string;
  } | null;
  error?: AuthError | Error | null;
  message?: string;
  sessionValid?: boolean;
}

/**
 * Centralized auth service for handling all authentication operations
 */
export const authService = {
  /**
   * Get the current user
   */
  getUser: async (): Promise<AuthResponse> => {
    try {
      const { data, error } = await supabase.auth.getUser();

      if (error) {
        return {
          status: 'error',
          error,
          message: error.message
        };
      }

      return {
        status: 'success',
        data: { user: data.user },
        message: 'User retrieved successfully'
      };
    } catch (error) {
      return {
        status: 'error',
        error: error as Error,
        message: 'Failed to get current user'
      };
    }
  },

  /**
   * Get the current session
   */
  getCurrentSession: async (): Promise<AuthResponse> => {
    try {
      const { data, error } = await supabase.auth.getSession();

      if (error) {
        return {
          status: 'error',
          error,
          message: error.message
        };
      }

      return {
        status: 'success',
        data: { session: data.session },
        message: 'Session retrieved successfully'
      };
    } catch (error) {
      return {
        status: 'error',
        error: error as Error,
        message: 'Failed to get current session'
      };
    }
  },

  /**
   * Sign up a new user
   * @param email User's email
   * @param password User's password
   * @param metadata Additional user metadata
   */
  signUp: async (email: string, password: string, metadata?: UserMetadata): Promise<AuthResponse> => {
    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            ...metadata,
            is_first_login: true // Add flag to track first login
          },
          emailRedirectTo: `${window.location.origin}/auth/callback`
        }
      });

      if (error) {
        return {
          status: 'error',
          error,
          message: error.message
        };
      }

      // Check if user already exists
      if (data.user?.identities?.length === 0) {
        return {
          status: 'error',
          message: 'User with this email already exists, please login'
        };
      }

      return {
        status: 'success',
        data: { user: data.user, session: data.session },
        message: 'Signup successful'
      };
    } catch (error) {
      return {
        status: 'error',
        error: error as Error,
        message: 'Failed to sign up'
      };
    }
  },

  // Create a new user with default password
  createUserWithDefaultPassword: async (email: string, metadata?: UserMetadata): Promise<AuthResponse> => {
    try {
      const defaultPassword = 'Test@1234';
      const { data, error } = await supabase.auth.signUp({
        email,
        password: defaultPassword,
        options: {
          data: {
            ...metadata,
            is_first_login: true // Add flag to track first login
          },
          emailRedirectTo: `${window.location.origin}/auth/callback`
        }
      });

      if (error) {
        return {
          status: 'error',
          error,
          message: error.message
        };
      }

      return {
        status: 'success',
        data: { user: data.user, session: data.session },
        message: 'User created successfully'
      };
    } catch (error) {
      return {
        status: 'error',
        error: error as Error,
        message: 'Failed to create user'
      };
    }
  },

  /**
   * Sign in an existing user
   * @param email User's email
   * @param password User's password
   */
  signIn: async (email: string, password: string): Promise<AuthResponse> => {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        return {
          status: 'error',
          error,
          message: error.message
        };
      }

      // Check if this is the user's first login
      const isFirstLogin = data.user?.user_metadata?.is_first_login === true;

      return {
        status: 'success',
        data: { 
          user: data.user, 
          session: data.session,
          url: isFirstLogin ? '/update-password?force=true' : undefined
        },
        message: 'Sign in successful',
      };
    } catch (error) {
      return {
        status: 'error',
        error: error as Error,
        message: 'Failed to sign in'
      };
    }
  },

  /**
   * Sign in with OAuth provider
   * @param provider OAuth provider (e.g., 'google')
   */
  signInWithOAuth: async (provider: Provider): Promise<AuthResponse> => {
    try {
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider,
        options: {
          redirectTo: `${window.location.origin}/auth/callback`
        }
      });

      if (error) {
        return {
          status: 'error',
          error,
          message: error.message
        };
      }

      // For OAuth, we return the URL to redirect to
      return {
        status: 'success',
        data: { url: data.url },
        message: 'OAuth sign in initiated'
      };
    } catch (error) {
      return {
        status: 'error',
        error: error as Error,
        message: 'Failed to initiate OAuth sign in'
      };
    }
  },

  /**
   * Sign out the current user
   */
  signOut: async (): Promise<AuthResponse> => {
    try {
      const { error } = await supabase.auth.signOut();

      if (error) {
        return {
          status: 'error',
          error,
          message: error.message
        };
      }

      return {
        status: 'success',
        message: 'Sign out successful'
      };
    } catch (error) {
      return {
        status: 'error',
        error: error as Error,
        message: 'Failed to sign out'
      };
    }
  },

  /**
   * Send password reset email to user
   * @param email User's email address
   */
  sendPasswordResetEmail: async (email: string): Promise<AuthResponse> => {
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${process.env.REACT_APP_SITE_URL}/auth/confirm?type=recovery&next=/update-password`
      });

      if (error) {
        return {
          status: 'error',
          error,
          message: error.message
        };
      }

      return {
        status: 'success',
        message: 'Password reset email sent successfully'
      };
    } catch (error) {
      return {
        status: 'error',
        error: error as Error,
        message: 'Failed to send password reset email'
      };
    }
  },

  /**
   * Update user's password
   * @param newPassword New password to set
   * @param options Additional options for password update
   */
  updatePassword: async (
    newPassword: string,
    options?: {
      code?: string;
      token_hash?: string;
      type?: 'recovery';
    }
  ): Promise<AuthResponse> => {
    try {
      // If we have a recovery code, exchange it for a session
      if (options?.code) {
        const { error: exchangeError } = await supabase.auth.exchangeCodeForSession(options.code);
        if (exchangeError) {
          return {
            status: 'error',
            error: exchangeError,
            message: exchangeError.message
          };
        }
      }

      // If we have a token hash, verify the OTP
      if (options?.token_hash && options?.type) {
        const { error: verifyError } = await supabase.auth.verifyOtp({
          type: options.type,
          token_hash: options.token_hash
        });
        if (verifyError) {
          return {
            status: 'error',
            error: verifyError,
            message: verifyError.message
          };
        }
      }

      // Check if we have a valid session
      const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
      if (sessionError || !sessionData.session) {
        return {
          status: 'error',
          error: sessionError,
          message: 'Authentication failed. Please try the reset process again.'
        };
      }

      // Update the password and remove first login flag
      const { error } = await supabase.auth.updateUser({
        password: newPassword,
        data: {
          is_first_login: false
        }
      });

      if (error) {
        return {
          status: 'error',
          error,
          message: error.message
        };
      }

      // Verify the session is still valid
      const { data: { session }, error: verifySessionError } = await supabase.auth.getSession();
      if (verifySessionError || !session) {
        return {
          status: 'error',
          error: verifySessionError,
          message: 'Password updated but session error occurred'
        };
      }

      return {
        status: 'success',
        message: 'Password updated successfully',
        sessionValid: true,
        data: {
          session,
          url: '/dashboard' // Redirect to dashboard after forced password change
        }
      };
    } catch (error) {
      return {
        status: 'error',
        error: error as Error,
        message: 'Failed to update password'
      };
    }
  },

  /**
   * Subscribe to auth state changes
   */
  onAuthStateChange: (callback: (event: AuthChangeEvent, session: Session | null) => void) => {
    return supabase.auth.onAuthStateChange(callback);
  },

  /**
   * Extract user profile data from Supabase user object
   * @param user Supabase user object
   * @returns Formatted user profile data
   */
  extractUserProfile: (user: User | null) => {
    if (!user) return null;

    const userMetadata = user.user_metadata || {};
    
    return {
      id: parseInt(user.id) || 0,
      name: userMetadata.name || userMetadata.full_name || user.user_metadata?.name || '',
      email: user.email || '',
      mobile: userMetadata.phoneNumber || userMetadata.phone || userMetadata.mobile || '',
      userName: userMetadata.username || userMetadata.user_name || userMetadata.userName || user.email?.split('@')[0] || '',
      role: (userMetadata.role || userMetadata.jobRole || 'PE') as "MANAGER" | "PE" | "ADMIN",
      status: (userMetadata.status || 'ACTIVE') as "ACTIVE" | "INACTIVE" | "BLOCKED",
      clients: userMetadata.clients || [],
      createdDate: user.created_at || new Date().toISOString(),
      lastLogin: user.last_sign_in_at || new Date().toISOString(),
      updatedDate: user.updated_at || new Date().toISOString(),
      profilePic: userMetadata.avatar_url || userMetadata.profile_pic || null,
      preSignedUrl: userMetadata.avatar_url || userMetadata.profile_pic || null,
      managerId: userMetadata.managerId || null
    };
  }
};